import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { FilterBar, FilterSearch } from "@/components/data-display/filter-bar";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingState } from "@/components/ui/system-state";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { AdminConsoleFailureState } from "@/features/admin/admin-console-failure";
import { formatCheckedAt, useServiceExplorer } from "@/features/admin/use-service-explorer";

/**
 * C5 — Configuration (ALL-5776). The non-secret effective configuration each
 * service reports to the admin console (GET /admin/api/service-explorer,
 * services[].config[]). Values are references and settings only; the console
 * never returns secrets here. Read-only.
 */
export default function AdminConfigViewerPage() {
  const { state, reload } = useServiceExplorer();
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    if (state.kind !== "ready") return [];
    const q = query.trim().toLowerCase();
    return state.explorer.services
      .map((service) => ({
        service,
        entries: service.config.filter(
          (entry) => !q || service.name.toLowerCase().includes(q) || `${entry.key} ${entry.value}`.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => !q || group.entries.length > 0)
      .sort((a, b) => a.service.name.localeCompare(b.service.name));
  }, [state, query]);

  const checkedAt = state.kind === "ready" ? formatCheckedAt(state.explorer.checkedAt) : undefined;

  return (
    <div>
      <Breadcrumb items={[{ label: "Services", href: "/admin/services" }, { label: "Configuration" }]} />
      <PageHeader
        title="Configuration"
        subtitle={
          checkedAt
            ? `Effective, non-secret settings each service reports. Read ${checkedAt}.`
            : "Effective, non-secret settings each service reports."
        }
      />

      {state.kind === "loading" && <LoadingState rows={4} />}

      {state.kind === "failed" && <AdminConsoleFailureState failure={state.failure} subject="service configuration" onRetry={reload} />}

      {state.kind === "ready" && (
        <>
          <FilterBar>
            <FilterSearch value={query} onChange={setQuery} placeholder="Search service, key or value" ariaLabel="Search configuration" />
          </FilterBar>
          {state.explorer.services.length === 0 ? (
            <div data-testid="config-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
              The console reported no services for this deployment, so there is no configuration to show.
            </div>
          ) : groups.length === 0 ? (
            <div data-testid="config-filtered-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
              No setting matches “{query.trim()}”.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map(({ service, entries }) => (
                <Card key={service.id} data-testid="config-service">
                  <CardBody>
                    <CardTitle>{service.name}</CardTitle>
                    {entries.length === 0 ? (
                      <p className="mt-2 text-sm text-muted">This service reports no settings.</p>
                    ) : (
                      <dl className="mt-2 divide-y divide-line">
                        {entries.map((entry, index) => (
                          <div key={`${entry.key}-${index}`} className="grid grid-cols-1 gap-0.5 py-2 sm:grid-cols-[minmax(160px,1fr)_2fr_auto] sm:gap-3">
                            <dt className="text-xs text-muted">{entry.key}</dt>
                            <dd className="min-w-0 break-words font-mono text-[13px] text-ink">{entry.value}</dd>
                            <dd className="text-xs text-subtle">{entry.source ?? ""}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
          <p className="mt-4 text-xs text-muted">
            Values are read-only here. To change organization settings, use <Link to="/settings" className="text-primary hover:underline">Settings</Link>.
          </p>
        </>
      )}
    </div>
  );
}
